<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'

const props = defineProps({
  dashboardName: {
    type: String,
    default: 'Dashboard'
  },
  exportData: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['modal-close'])
const toast = useToast()

const base64String = ref('')
const copied = ref(false)

onMounted(() => {
  base64String.value = btoa(JSON.stringify(props.exportData))
})

async function copyToClipboard() {
  try {
    await navigator.clipboard.writeText(base64String.value)
    copied.value = true
    toast.success('Copied to clipboard')
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    toast.error('Failed to copy')
  }
}
</script>

<template>
  <div class="export-modal">
    <h3 class="modal-title">Export: {{ dashboardName }}</h3>
    <p class="modal-description">Copy the encoded string below and share it to import elsewhere.</p>

    <textarea
      class="export-textarea"
      :value="base64String"
      readonly
      @focus="$event.target.select()"
    />

    <div class="modal-actions">
      <button class="btn-copy" @click="copyToClipboard">
        <font-awesome-icon :icon="['fas', copied ? 'check' : 'copy']" />
        {{ copied ? 'Copied' : 'Copy' }}
      </button>
      <button class="btn-close-modal" @click="emit('modal-close')">
        Close
      </button>
    </div>
  </div>
</template>

<style scoped>
.export-modal {
  width: 500px;
  max-width: 90vw;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #f3f4f6;
  margin: 0 0 0.5rem 0;
}

.modal-description {
  font-size: 0.8125rem;
  color: #9ca3af;
  margin: 0 0 1rem 0;
}

.export-textarea {
  width: 100%;
  height: 160px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 0.75rem;
  color: #d1d5db;
  font-family: monospace;
  font-size: 0.75rem;
  resize: vertical;
  word-break: break-all;
}

.export-textarea:focus {
  outline: none;
  border-color: rgba(115, 87, 210, 0.4);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn-copy {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(115, 87, 210, 0.15);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.375rem;
  color: #7357D2;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-copy:hover {
  background: rgba(115, 87, 210, 0.25);
  border-color: rgba(115, 87, 210, 0.5);
}

.btn-close-modal {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  color: #9ca3af;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #d1d5db;
}
</style>
